import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { Button } from '@mobi/component-library/Base/Buttons'
import { layering } from '@mobi/component-library/Foundations'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import { ShareBetSelection } from '@core/Areas/BetLoop/Components/QuickShare/Components/ShareBetSelection'
import { usePostMultipleSharesToBetLoopMutation } from '@core/Areas/BetLoop/Components/QuickShare/hooks/usePostMultipleSharesToBetLoopMutation'
import type { BetLoopKambiBetPayload } from '@core/Areas/BetLoop/helpers/api/types'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { isKambiSharedCoupon } from '@core/Areas/BetLoop/helpers/typeGuards/isKambiSharedCoupon'
import { FirstPostConfirmation } from '../Common/FirstPostConfirmation'
import type {
  KambiTicketsToShare,
  SelectedKambiTickets,
  SelectedTickets,
  TicketsToShare,
} from './types'

export const QuickShare: React.FC<{ isOpen: boolean; onDismiss(): void }> = ({
  isOpen,
  onDismiss,
}) => {
  const selectedTicketsRef = React.useRef<SelectedTickets>(new Map())
  const selectedKambiTicketsRef = React.useRef<SelectedKambiTickets>(new Map())

  const [isFirstShareModalOpen, setIsFirstShareModalOpen] = React.useState(false)
  const [shouldHidePostButton, setShouldHidePostButton] = React.useState(false)
  const [ticketsToShare, setTicketsToShare] = React.useState<TicketsToShare>(() => new Set())
  const [kambiTicketsToShare, setKambiTicketsToShare] = React.useState<KambiTicketsToShare>(
    () => new Set()
  )

  React.useEffect(() => {
    if (isOpen) return
    return () => {
      selectedTicketsRef.current = new Map()
      selectedKambiTicketsRef.current = new Map()
      setIsFirstShareModalOpen(false)
      setTicketsToShare(new Set())
      setKambiTicketsToShare(new Set())
      setShouldHidePostButton(false)
    }
  }, [isOpen])

  const { data: profileData, isLoading: isLoadingProfile } = useQuery(betLoopPrincipleProfileQuery)

  const { mutate: onPostToBetLoopMutate, isPending } = usePostMultipleSharesToBetLoopMutation({
    ticketsToShare,
    kambiTicketsToShare,
    selectedTicketsRef,
    selectedKambiTicketsRef,
    onDismiss,
    setIsFirstShareModalOpen,
  })

  const handleKambiTicketClick = (
    betRef: KambiHistoryCoupon['bets'][0]['betRef'],
    ticket: KambiHistoryCoupon
  ) => {
    setKambiTicketsToShare(prevTickets => {
      const payload: BetLoopKambiBetPayload = { betRef, couponRef: ticket.couponRef }

      const newTickets = new Set(prevTickets)
      const hasTicket = [...newTickets].some(x => x.betRef === betRef)

      if (hasTicket) {
        ;[...newTickets].forEach(x => {
          if (x.betRef === betRef) newTickets.delete(x)
        })
        selectedKambiTicketsRef.current.delete(betRef)
      } else {
        newTickets.add(payload)
        selectedKambiTicketsRef.current.set(betRef, {
          coupon: ticket,
          couponRef: ticket.couponRef,
        })
      }
      return newTickets
    })
  }

  const handleTicketClick = (
    ticketId: Ticket['ticketSequence'] | KambiHistoryCoupon['bets'][0]['betRef'],
    ticket: Ticket | KambiHistoryCoupon
  ) => {
    if (isPending) return

    if (isKambiSharedCoupon(ticket)) {
      handleKambiTicketClick(ticketId, ticket)
    } else {
      setTicketsToShare(prevTickets => {
        const newTickets = new Set(prevTickets)
        if (newTickets.has(ticketId)) {
          newTickets.delete(ticketId)
          selectedTicketsRef.current.delete(ticketId)
        } else {
          newTickets.add(ticketId)
          selectedTicketsRef.current.set(ticketId, ticket)
        }
        return newTickets
      })
    }
  }

  const handleShareToBetLoopButtonClick = () => {
    if (profileData?.shareCount === 0) {
      setIsFirstShareModalOpen(true)
    } else {
      if (ticketsToShare.size > 0 || kambiTicketsToShare.size > 0) onPostToBetLoopMutate()
    }
  }

  const betsToShareCount = ticketsToShare.size + kambiTicketsToShare.size

  return createPortal(
    <Tray
      isOpen={isOpen}
      headerTitle='Pending'
      onDismiss={onDismiss}
      footerComponent={
        shouldHidePostButton ? null : (
          <Button
            size='md'
            isFullWidth
            color='primary'
            isLoading={isPending}
            onClick={handleShareToBetLoopButtonClick}
            disabled={betsToShareCount === 0 || isLoadingProfile}
          >
            Share {betsToShareCount > 1 ? `${betsToShareCount} Bets` : 'Bet'} to Bet Loop
          </Button>
        )
      }
    >
      <>
        <ShareBetSelection
          ticketsToShare={ticketsToShare}
          kambiTicketsToShare={kambiTicketsToShare}
          onEmptyState={setShouldHidePostButton}
          onTicketClick={handleTicketClick}
        />

        {createPortal(
          <FirstPostConfirmation
            zIndex={layering.overlayHigh}
            isOpen={isFirstShareModalOpen}
            onConfirm={() => {
              if (ticketsToShare.size > 0 || kambiTicketsToShare.size > 0) onPostToBetLoopMutate()
            }}
            onCancel={() => setIsFirstShareModalOpen(false)}
          />,
          document.body
        )}
      </>
    </Tray>,
    document.body
  )
}
